/**
 * Wizard „Neue Kampagne“ (Recruiting): Kopf, Stepper, Schritt-Inhalt, Fußleiste.
 * Layout und Maße folgen dem Design `Neue Kampagne.dc.html` (Board „Geführt in 7 Schritten“).
 */

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { Routes, ZoomLevels } from 'enums';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { makeStyles } from 'tss-react/mui';
import RunningCampaignNotice from './components/running-campaign-notice';
import SearchRestartDialog from './components/search-restart-dialog';
import { MiSpinner, WizGradientButton } from './components/wizard-ui';
import { wz } from './components/wizard-tokens';
import RunView from './run-view';
import GapDialog from './steps/job/gap-dialog';
import { hasPlaceholders, redPoints } from './steps/job/job-gate.logic';
import StepApproach from './steps/step-approach';
import StepCandidateList from './steps/step-candidate-list';
import StepCandidates from './steps/step-candidates';
import StepJob from './steps/step-job';
import StepJudging from './steps/step-judging';
import StepMessages from './steps/step-messages';
import StepReview from './steps/step-review';
import { useCampaignWizard, WizardController } from './use-campaign-wizard';
import { isStepDone } from './campaign-wizard.logic';
import { isWorkWindowMissingError } from './start-error.logic';
import { WizardStep } from './campaign-wizard.types';

const STEP_LABEL_KEYS = [
  'wizStepJob',
  'wizStepCandidates',
  'wizStepJudging',
  'wizStepList',
  'wizStepApproach',
  'wizStepMessages',
  'wizStepReview',
] as const;

const BLOCKER_KEYS: Record<string, string> = {
  briefing: 'wizMissingBriefing',
  name: 'wizMissingName',
  account: 'wizMissingAccount',
  locations: 'wizMissingLocations',
  searchUrl: 'wizMissingSearchUrl',
  classes: 'wizMissingClasses',
  bonus: 'wizMissingBonus',
  calendarUrl: 'wizMissingCalendarUrl',
  inmailCredit: 'wizMissingInmailCredit',
  workWindow: 'wizMissingWorkWindow',
  approveChoice: 'wizMissingApproveChoice',
  recontactChoice: 'wizMissingRecontactChoice',
};

const renderStep = (step: WizardStep, wizard: WizardController) => {
  switch (step) {
    case 1:
      return <StepJob wizard={wizard} />;
    case 2:
      return <StepCandidates wizard={wizard} />;
    case 3:
      return <StepJudging wizard={wizard} />;
    case 4:
      return <StepCandidateList wizard={wizard} />;
    case 5:
      return <StepApproach wizard={wizard} />;
    case 6:
      return <StepMessages wizard={wizard} />;
    default:
      return <StepReview wizard={wizard} />;
  }
};

const CampaignWizard: React.FC = () => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const wizard = useCampaignWizard();
  const { step } = wizard;

  // Beim Schrittwechsel an den Anfang springen (der Scrollbereich bleibt sonst stehen).
  useEffect(() => {
    document.getElementById('wizard-scroll')?.scrollTo({ top: 0 });
  }, [step]);

  if (wizard.loading) {
    return (
      <div className={classes.container}>
        <div className={classes.center}>
          <MiSpinner size={32} />
          <div className={classes.muted}>{t('wizLoading')}</div>
        </div>
      </div>
    );
  }

  if (wizard.loadError) {
    return (
      <div className={classes.container}>
        <div className={classes.center}>
          <div className={classes.error}>{t('wizLoadError')}</div>
          <div className={classes.muted}>{wizard.loadError}</div>
          <button type="button" className={classes.linkButton} onClick={wizard.cancel}>
            {t('wizBackToCampaigns')}
          </button>
        </div>
      </div>
    );
  }

  if (wizard.running) {
    return (
      <div className={classes.container}>
        <RunView wizard={wizard} />
      </div>
    );
  }

  if (wizard.locked) {
    return (
      <div className={classes.container}>
        <RunningCampaignNotice onDuplicate={wizard.duplicateCampaign} onBack={wizard.cancel} />
      </div>
    );
  }

  const isLast = step === 7;
  // Fehlende Konto-Arbeitszeiten (Schritt 7) stehen von Anfang an da, nicht erst nach dem Klick auf „Kampagne starten“.
  const workWindowBlocked = step === 7 && wizard.blocker === 'workWindow';
  const blockerKey =
    (wizard.showBlocker || workWindowBlocked) && wizard.blocker ? BLOCKER_KEYS[wizard.blocker] : undefined;
  const message =
    wizard.startError ??
    (wizard.nameTaken
      ? t('wizNameTaken')
      : wizard.notice
        ? wizard.notice
        : wizard.saveError
          ? `${t('wizSaveError')} ${wizard.saveError}`
          : blockerKey
            ? t(blockerKey)
            : step >= 5 && wizard.flowError
              ? wizard.flowError
              : null);
  const maxWidth = step === 4 ? 1000 : 780;

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.headerTop}>
          <button type="button" className={classes.back} onClick={wizard.cancel}>
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
            {t('wizBackToCampaigns')}
          </button>
          <button type="button" className={classes.legacySwitch} onClick={() => void wizard.openLegacySetup()}>
            {t('wizLegacySetup')}
          </button>
        </div>
        <div className={classes.titleBlock}>
          <div className={classes.title}>{t('wizTitle')}</div>
          <div className={classes.stepper}>
            {STEP_LABEL_KEYS.map((key, i) => {
              const n = (i + 1) as WizardStep;
              // Fortschritt: alles vor dem höchsten erreichten Schritt zählt als erledigt (außer dem angezeigten).
              const done = isStepDone(n, step, wizard.state.maxStep);
              const cur = n === step;
              return (
                <button
                  key={key}
                  type="button"
                  className={classes.stepItem}
                  onClick={() => wizard.goTo(n)}
                  aria-current={cur ? 'step' : undefined}
                >
                  <span
                    className={classes.stepCircle}
                    style={{
                      background: cur || done ? wz.primary : wz.border,
                      color: cur || done ? '#FFF' : wz.muted,
                    }}
                  >
                    {done ? '✓' : n}
                  </span>
                  <span
                    className={classes.stepLabel}
                    style={{ fontWeight: cur ? 700 : 400, color: cur ? wz.text : wz.muted }}
                  >
                    {t(key)}
                  </span>
                  {n < 7 ? <span className={classes.stepLine} /> : null}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className={classes.scroll} id="wizard-scroll">
        <div className={classes.content} style={{ maxWidth }}>
          {renderStep(step, wizard)}
        </div>
      </div>

      <GapDialog
        open={wizard.gate.status === 'warn'}
        points={redPoints(wizard.state.adCheck)}
        placeholders={hasPlaceholders(wizard.state.briefingExtras)}
        onAmend={wizard.gateAmend}
        onProceed={wizard.gateProceed}
      />

      <SearchRestartDialog
        open={wizard.searchRestartPending}
        onConfirm={wizard.confirmSearchRestart}
        onCancel={wizard.cancelSearchRestart}
      />

      <div className={classes.footer}>
        <button type="button" className={cx(classes.prev)} onClick={wizard.prev}>
          {step === 1 ? t('wizCancel') : t('wizBack')}
        </button>
        {message ? (
          <div className={classes.message}>
            {message}
            {(wizard.startError && isWorkWindowMissingError(wizard.startErrorCode)) ||
            (!wizard.startError && workWindowBlocked) ? (
              <>
                {' '}
                <Link to={Routes.RECRUITING_BOOSTER_CO_PILOT_ACCOUNTS} className={classes.messageLink}>
                  {t('wizStartWorkWindowLink')}
                </Link>
              </>
            ) : null}
          </div>
        ) : (
          <div style={{ flex: 1 }} />
        )}
        <div className={classes.actions}>
          {isLast ? (
            <button
              type="button"
              className={classes.draft}
              onClick={() => void wizard.saveDraft()}
              disabled={wizard.busy}
            >
              {t('wizSaveDraft')}
            </button>
          ) : null}
          <WizGradientButton
            onClick={() => void wizard.next()}
            loading={wizard.busy}
            disabled={wizard.accountsLoading && step === 1}
            icon={
              isLast ? (
                <RocketLaunchIcon sx={{ fontSize: 18 }} />
              ) : (
                <ArrowForwardIcon sx={{ fontSize: 18 }} />
              )
            }
          >
            {isLast ? t('wizStart') : t('wizNext')}
          </WizGradientButton>
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles()(theme => ({
  container: {
    background: theme.palette.backgrounds.main_new,
    height: 'calc(100vh - 60px - 32px)',
    borderRadius: 6,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    fontSize: theme.typography.pxToRem(14),
    color: wz.text,
    lineHeight: 'normal',

    [theme.breakpoints.down('xl')]: { height: `calc((100vh / ${ZoomLevels.XL}) - 60px - 32px)` },
    [theme.breakpoints.down(1300)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('lg')]: { height: `calc((100vh / ${ZoomLevels.LG}) - 60px - 32px)` },
    [theme.breakpoints.down(1000)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_MD}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('md')]: { height: `calc((100vh / ${ZoomLevels.MD}) - 60px - 32px)` },
  },
  center: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  muted: { color: wz.muted, fontSize: 14 },
  error: { color: wz.redFg, fontSize: 15, fontWeight: 700 },
  linkButton: {
    color: wz.primary,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textDecoration: 'underline',
    fontSize: 14,
    fontFamily: 'inherit',
  },
  header: { padding: '24px 40px 0', display: 'flex', flexDirection: 'column', gap: 18 },
  headerTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 },
  legacySwitch: {
    fontSize: 13,
    color: wz.muted,
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    textDecoration: 'underline',
    fontFamily: 'inherit',
  },
  back: {
    fontSize: 14,
    color: wz.primary,
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    cursor: 'pointer',
    width: 'fit-content',
    background: 'transparent',
    border: 'none',
    padding: 0,
    fontFamily: 'inherit',
  },
  titleBlock: { display: 'flex', flexDirection: 'column', gap: 16 },
  title: { fontSize: 26, fontWeight: 500, letterSpacing: '-0.2px' },
  stepper: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  stepItem: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: 'transparent',
    border: 'none',
    padding: 0,
    fontFamily: 'inherit',
  },
  stepCircle: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 700,
  },
  stepLabel: { fontSize: 14 },
  stepLine: { width: 28, height: 1, background: wz.dashed, marginLeft: 2 },
  scroll: {
    flex: 1,
    overflow: 'auto',
    padding: '20px 40px 24px',
    display: 'flex',
    justifyContent: 'center',
  },
  content: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    alignSelf: 'flex-start',
  },
  footer: {
    background: '#FFF',
    borderTop: `1px solid ${wz.border}`,
    padding: '14px 88px 14px 40px', // rechts Platz für den Chat-Button der App
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 20,
  },
  prev: {
    fontSize: 14,
    color: wz.text,
    textDecoration: 'underline',
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    padding: 0,
    fontFamily: 'inherit',
  },
  message: { flex: 1, textAlign: 'right', fontSize: 13, color: wz.redFg },
  messageLink: { color: wz.redFg, fontWeight: 700, textDecoration: 'underline' },
  actions: { display: 'flex', alignItems: 'center', gap: 20 },
  draft: {
    fontSize: 14,
    color: wz.primary,
    textDecoration: 'underline',
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    padding: 0,
    fontFamily: 'inherit',
  },
}));

export default CampaignWizard;
